import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero } from "@bluebottlecoffee/design-system/components";
import { CanonicalHead } from "../../../components/CanonicalHead";
import { globalModulesProjection } from "../../../lib/sanity/global-modules";
import sanityClient from "../../../lib/sanity-client";
import renderModule from "../../../lib/render-module";
import { toSuperHeroProps } from "../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../lib/utils";
import { OurCoffee } from "../../../lib/sanity-schema";
import { BaseProps } from "../../../lib/sanity/shared";
import { filters } from "../../../lib/sanity/utils";
import { resolveTabbedCollections } from "../../../lib/resolve-tabbed-collections";

interface PageData extends BaseProps {
  ourCoffee: OurCoffee;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);
  const { lang, region } = defaultProps;

  const ourCoffee = await sanityClient().fetch<OurCoffee>(`*[
    ${filters.whereType("ourCoffee")} &&
    ${filters.excludeDrafts}
  ][0]{
    ...,
    modules[] {
      ...,
      ${globalModulesProjection(lang)},
    },
  }`);

  await resolveTabbedCollections(ourCoffee?.modules, { lang, region });

  return {
    props: {
      ourCoffee,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: OurCoffee) {
  if (!data.title) {
    throw Error("Coffee page must have a title.");
  }
}

const OurCoffeePage: FunctionComponent<PageData> = ({
  ourCoffee,
  ...layoutWrapperProps
}) => {
  validate(ourCoffee);
  const { region, lang, aria } = layoutWrapperProps;

  return (
    <>
      <CanonicalHead region={region} lang={lang} slug="our-coffee" />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,
          pageTitle: ourCoffee.title[lang],
          metaTags: ourCoffee.metaTags,
        }}
        isTransparent
      >
        {ourCoffee.hero?.length > 0 && (
          <SuperHero
            {...toSuperHeroProps(ourCoffee.hero, aria, region, lang)}
          />
        )}
        {ourCoffee.modules?.length > 0 &&
          ourCoffee.modules.map((module) =>
            renderModule({ module, region, lang, aria }),
          )}
      </LayoutWrapper>
    </>
  );
};

export default OurCoffeePage;
